import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { Allotment } from 'allotment'
import { AxiosError } from 'axios'
import produce from 'immer'
import { filter, find, findIndex, isEqual, reject, some } from 'lodash-es'
import { useDebouncedCallback } from 'use-debounce'
import { v4 } from 'uuid'
import { InsertDriveFile, PlaylistAdd, PostAdd } from '@mui/icons-material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined'
import NoteAddOutlined from '@mui/icons-material/NoteAddOutlined'
import { Box, IconButton, Stack } from '@mui/material'
import { downloadFile } from '@api/file-download/file-download'
import { startConvertFileToPdfTask } from '@api/file-pdf-conversion/file-pdf-conversion'
import { Core } from '@pdftron/webviewer'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AutosaveIndicator } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { Info, TooltipIconButton } from '@ui/feedback'
import { DropzoneBox } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { LayoutDrawer } from '@ui/structure'
import {
  useAvailableTemplates,
  useBlockAction,
  useEnterEditorPage,
  useGenerateTemplateFiles,
  useNotification,
  useProjectFiles,
  useUnlinkFile,
  useUploadProjectDocumentationFile,
} from '@editorHooks'
import {
  Viewer,
  ViewerDocument,
  ViewerMimeTypes,
  ViewerRef,
  exportDocumentAnnotations,
} from '@editorComponents'
import { getProjectFile, getProjectFiles, getProjectFilesTemplates } from '@queries'
import { downLoadFile } from '@utils'
import {
  addTemplates,
  changeFileOrder,
  deleteProjectFile,
  generateTemplatePreview,
  postFileMetadata,
  postFolderConfig,
  unlinkProjectFile,
  updateFile,
} from '@mutations'
import {
  documentationRootGuid,
  fullDocumentationRootGuid,
  projectFilesRootGuid,
} from 'src/constants/files'
import { retryUntilSuccessOrFailure } from 'src/state/utils'
import { FullStructuralDocumentsList } from './components'
import AddFolderModal from './components/AddFolderModal'
import AddTemplateModal from './components/AddTemplateModal'
import DocumentGenerationFooter from './components/DocumentGenerationFooter'
import DocumentNameModal from './components/DocumentNameModal'
import FileOrdering from './components/FileOrdering'
import { getExcludedPages } from './components/PageSelectionField/utils'
import PreviewPlaceholder from './components/PreviewPlaceholder'
import PropertiesPane from './components/PropertiesPane'
import RunningDocumentTasksFooter from './components/RunningDocumentTasksFooter'
import UploadDocumentModal from './components/UploadDocumentModal'
import { PROPERTIES_BLOCK_ACTION, STRUCTURE_BLOCK_ACTION } from './constants'
import { AttachmentEditing, SaveState } from './types'
import { findStaleFullDocuments, getAddedTemplateGuids } from './utils'

const defaultDebounceDelay = 5000
const fileSaveDebounceDelay = 30000

const DocumentManager = (): ReactElement => {
  useEnterEditorPage('document-manager')

  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const viewerRef = useRef<ViewerRef | null>(null)

  // STATE

  const [selectedDocument, setSelectedDocument] = useState<string | null>(null)

  // Parent guid the upload dialog uploads into, null while the dialog is closed
  const [uploadDocumentParentGuid, setUploadDocumentParentGuid] = useState<string | null>(null)
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [documentNameModalOpen, setDocumentNameModalOpen] = useState(false)
  // "Dokument erzeugen" runs still in progress (Word generation, PDF conversion and
  // loading the result), by run id. Each run clears only its own entry, so concurrent
  // runs don't affect each other's loading state.
  const [generatingRunIds, setGeneratingRunIds] = useState<string[]>([])
  const isGenerating = generatingRunIds.length > 0

  const [cropConfig, setCropConfig] = useState<{
    croppedLeft: number
    croppedTop: number
    croppedRight: number
    croppedBottom: number
  } | null>(null)

  const [showFullDocuments, setShowFullDocuments] = useState(false)

  // The viewer shows the file attached to the selected markdown page ("Datei" tab)
  // instead of the preview of the page ("Template" tab)
  const [isShowingAttachment, setIsShowingAttachment] = useState(false)

  // Page count of the document last loaded into the viewer
  const [viewerPageCount, setViewerPageCount] = useState<{
    guid: string
    pageCount: number
  } | null>(null)

  // Page selection being edited, previewed before its debounced save lands
  const [editedPageSelection, setEditedPageSelection] = useState<{
    guid: string
    pages: number[] | null
  } | null>(null)

  const onPagesChange = useCallback(
    (guid: string, pages: number[] | null) => setEditedPageSelection({ guid, pages }),
    [],
  )

  // DATA FETCHING

  const {
    data: documentTreeData,
    isFetching: isLoadingDocumentTree,
    isLoading: isInitiallyLoadingDocumentTree,
  } = useProjectFiles(documentationRootGuid)
  const { data: mergedDocumentsData } = useProjectFiles(fullDocumentationRootGuid)

  const selectedFile = useMemo(() => {
    const guid = selectedDocument || documentTreeData?.files[0]?.guid || null

    if (guid) {
      const document = find([...documentTreeData.files, ...mergedDocumentsData.files], { guid }) as
        | FileItem
        | undefined
      return document || null
    }

    return null
  }, [documentTreeData.files, mergedDocumentsData.files, selectedDocument])

  const shownAttachment =
    isShowingAttachment && !showFullDocuments ? selectedFile?.attachment : undefined
  const isShownAttachmentPdf = shownAttachment?.mimetype === 'application/pdf'

  // The viewer shows the attached file on the "Datei" tab, the page itself otherwise
  const selectedFilePageCount =
    viewerPageCount && viewerPageCount.guid === (shownAttachment?.guid ?? selectedFile?.guid)
      ? viewerPageCount.pageCount
      : undefined

  // Pages of an uploaded PDF left out of the Statikdokument, marked in the viewer. The
  // selection of an attached PDF is stored on the page, like its crop margins
  const excludedPages = useMemo(() => {
    const isUploadedPdf =
      !selectedFile?.template_key && selectedFile?.mimetype === 'application/pdf'
    if (!selectedFile || !selectedFilePageCount || !(isUploadedPdf || isShownAttachmentPdf)) {
      return undefined
    }

    const pages =
      editedPageSelection?.guid === selectedFile.guid
        ? editedPageSelection.pages
        : selectedFile.additional_data?.pages

    return getExcludedPages(selectedFilePageCount, pages)
  }, [selectedFile, selectedFilePageCount, isShownAttachmentPdf, editedPageSelection])

  const selectedSection = useMemo(() => {
    const guid = selectedDocument || null

    if (guid) {
      const folder = find(documentTreeData.sections, { guid }) as SectionItem | undefined
      return folder || null
    }

    return null
  }, [documentTreeData, selectedDocument])

  const markdownPageTemplatePayload = useMemo(
    (): TemplatesResult => ({
      templates: [
        {
          guid: 'markdown-page',
          parent_guid: '',
          name: 'Markdown-Seite',
          type: 'file',
          children: [],
        },
      ],
    }),
    [],
  )

  // MUTATIONS

  const {
    mutateAsync: mutateSort,
    isLoading: isMutatingSort,
    error: sortError,
    variables: sortVariables,
  } = useMutation(
    async (ordering: FileOrder[]) => {
      return changeFileOrder.request(projectId, documentationRootGuid, ordering)
    },
    {
      onError: () => {
        enqueueSnackbar('Fehler beim Speichern der Sortierung', { variant: 'error' })
      },
      retry: 3,
      retryDelay: 1000,
    },
  )

  const debouncedSortSave = useDebouncedCallback(mutateSort, sortError ? 0 : defaultDebounceDelay)

  const {
    mutateAsync: mutateFileConfig,
    isLoading: isMutatingFileConfig,
    error: fileConfigError,
    variables: fileConfigVariables,
  } = useMutation({
    mutationFn: async (data: FileItemConfig) => {
      const responseData = await postFileMetadata.request(projectId, documentationRootGuid, data)
      return responseData
    },
    onSuccess: (data: FileItem) => {
      const queryKey = getProjectFiles.getKey(projectId, documentationRootGuid)

      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return

          const index = findIndex(draft.files, { guid: data.guid })
          if (index !== -1) {
            draft.files[index].name = data.name
            draft.files[index].additional_data = data.additional_data
            // Changing a markdown page drops its outdated preview. Other files keep their url:
            // the response carries a freshly signed one, which would reload the viewer
            if (draft.files[index].template_key === 'markdown-page') {
              draft.files[index].url = data.url
            }
          }
        }),
      )
      // The saved value now drives the excluded-page preview, unless a newer edit is pending
      setEditedPageSelection(current =>
        current?.guid === data.guid &&
        isEqual(current.pages ?? null, data.additional_data?.pages ?? null)
          ? null
          : current,
      )
    },
    // The unsaved preview is kept, so it keeps matching the selection shown in the form
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Eigenschaften'), {
        variant: 'error',
      })
    },
  })

  const debouncedFileConfigSave = useDebouncedCallback(mutateFileConfig, fileSaveDebounceDelay)

  const {
    mutateAsync: mutateUpdateFile,
    isLoading: isMutatingUpdateFile,
    error: updateFileError,
    variables: updateFileVariables,
  } = useMutation({
    mutationFn: async (data: { item_guid: string; file: File }) => {
      debouncedFileSave.cancel()
      return updateFile.request(projectId, documentationRootGuid, data.item_guid, data.file)
    },
    onSuccess: async (data: FileItem) => {
      const queryKey = getProjectFiles.getKey(projectId, documentationRootGuid)

      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return

          const index = findIndex(draft.files, { guid: data.guid })
          if (index !== -1) {
            draft.files[index].url = data.url
          }
        }),
      )

      // A page selection refers to the replaced file's pages, so it is reset to all pages
      await debouncedFileConfigSave.flush()
      const replacedFile = find(queryClient.getQueryData<FilesResult>(queryKey)?.files, {
        guid: data.guid,
      })
      if (replacedFile?.mimetype === 'application/pdf' && replacedFile.additional_data?.pages) {
        setEditedPageSelection(current => (current?.guid === data.guid ? null : current))
        await mutateFileConfig({
          ...replacedFile.additional_data,
          item_guid: replacedFile.guid,
          name: replacedFile.name,
          pages: null,
        } as PdfFileItemConfig)
        enqueueSnackbar('Die Seitenauswahl wurde für die neue Datei zurückgesetzt', {
          variant: 'info',
        })
      }
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Speichern der Eigenschaften', { variant: 'error' })
    },
  })

  const {
    mutateAsync: mutateFolderConfig,
    isLoading: isMutatingFolderConfig,
    error: folderConfigError,
    variables: folderConfigVariables,
  } = useMutation({
    mutationFn: async (data: FolderItemConfig) => {
      return postFolderConfig.request(projectId, documentationRootGuid, data)
    },
    onSuccess: (data: SectionItem) => {
      const queryKey = getProjectFiles.getKey(projectId, documentationRootGuid)

      queryClient.setQueryData<FilesResult>(
        queryKey,
        produce<FilesResult>(draft => {
          if (!draft) return

          const index = findIndex(draft.sections, { guid: data.guid })
          if (index !== -1) {
            draft.sections[index].name = data.name
            draft.sections[index].additional_data = data.additional_data
          }
        }),
      )
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Speichern der Eigenschaften', { variant: 'error' })
    },
  })

  const debouncedFolderConfigSave = useDebouncedCallback(mutateFolderConfig, fileSaveDebounceDelay)

  const { mutateAsync: uploadDocument, isLoading: isUploadingDocument } =
    useUploadProjectDocumentationFile(documentationRootGuid)

  const {
    mutateAsync: mutateFileSave,
    isLoading: isMutatingFileSave,
    error: fileSaveError,
    variables: fileSaveVariables,
  } = useMutation({
    mutationFn: async ({
      doc,
      fileGuid,
      rootGuid,
      isPdf,
    }: {
      fileGuid: string
      rootGuid: string
      isPdf: boolean
      doc: Core.Document
    }) => {
      // Export the current document as a DOCX blob
      const core = viewerRef.current?.viewerInstance?.Core
      // The viewer's annotations only belong to `doc` while it is still the loaded document;
      // selecting another document flushes pending saves first, so a mismatch is only a stray call
      const annotationManager =
        core?.documentViewer.getDocument() === doc ? core.annotationManager : undefined
      if (isPdf && !annotationManager) return
      // Keep the viewer's preview overlays (crop margins, excluded pages) out of the file
      const xfdfString =
        isPdf && annotationManager ? await exportDocumentAnnotations(annotationManager) : undefined
      const docxBlob = await doc
        .getFileData({
          includeAnnotations: isPdf,
          xfdfString,
          downloadType: isPdf ? 'pdf' : 'docx',
        })
        .then(
          data =>
            new Blob([data], {
              type: isPdf
                ? 'application/pdf'
                : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            }),
        )

      if (docxBlob) {
        return updateFile.request(projectId as string, rootGuid, fileGuid, docxBlob)
      }
    },
    onError: () => {
      enqueueSnackbar('Fehler beim Autospeichern des Dokuments', {
        variant: 'error',
      })
    },
  })

  const debouncedFileSave = useDebouncedCallback(mutateFileSave, fileSaveDebounceDelay, {
    maxWait: 1000 * 60 * 3,
  })

  // SYNC STATE HANDLING

  // The structure (order, added items) and the properties of the selected item
  // (its data and document content) are saved separately
  const [hasPendingStructureChanges, setHasPendingStructureChanges] = useState(false)
  const [hasPendingPropertiesChanges, setHasPendingPropertiesChanges] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setHasPendingStructureChanges(debouncedSortSave.isPending())
      setHasPendingPropertiesChanges(
        debouncedFileConfigSave.isPending() ||
          debouncedFolderConfigSave.isPending() ||
          debouncedFileSave.isPending(),
      )
    }, 100)

    return () => clearInterval(interval)
  }, [debouncedSortSave, debouncedFileConfigSave, debouncedFolderConfigSave, debouncedFileSave])

  // When nothing is pending, flush() returns the last save's (possibly failed) promise again,
  // so only pending saves are flushed
  const flushStructure = async () => {
    if (debouncedSortSave.isPending()) await debouncedSortSave.flush()
  }

  const flushProperties = async () => {
    await Promise.all([
      debouncedFileConfigSave.isPending() ? debouncedFileConfigSave.flush() : undefined,
      debouncedFolderConfigSave.isPending() ? debouncedFolderConfigSave.flush() : undefined,
      debouncedFileSave.isPending() ? debouncedFileSave.flush() : undefined,
    ])
  }

  const flushAll = async () => {
    await Promise.all([flushStructure(), flushProperties()])
  }

  const { mutateAsync: mutateAddTemplates, isLoading: isAddingTemplates } = useMutation({
    mutationFn: async (keys: TemplatesResult) =>
      addTemplates.request(projectId, documentationRootGuid, {
        ...keys,
        move_custom_files_to_template_position: true,
      }),
    onSuccess: ({ files, sections }) => {
      // Placed element files are no longer offered
      queryClient.invalidateQueries(
        getProjectFilesTemplates.getKey(projectId, documentationRootGuid),
      )
      // With move_custom_files_to_template_position the response is the whole
      // tree after the save, so the mutation (and the add-template modal) only
      // finishes once the new items are in the loaded tree, without a refetch
      queryClient.setQueryData<FilesResult>(
        getProjectFiles.getKey(projectId, documentationRootGuid),
        { files, sections },
      )
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hinzufügen der Templates'), {
        variant: 'error',
        persist: true,
      })
    },
  })

  const structureSaveState: SaveState = {
    isSaving: isMutatingSort || isUploadingDocument || isAddingTemplates,
    // Background refetches too, a reload of the structure should not be invisible
    isLoading: isLoadingDocumentTree,
    // A failed save is not "saved" either (the error itself is shown as a snackbar)
    isDirty: hasPendingStructureChanges || !!sortError,
  }
  const propertiesSaveState: SaveState = {
    isSaving:
      isMutatingFileConfig || isMutatingFolderConfig || isMutatingFileSave || isMutatingUpdateFile,
    isLoading: isInitiallyLoadingDocumentTree,
    isDirty:
      hasPendingPropertiesChanges ||
      !!fileConfigError ||
      !!folderConfigError ||
      !!fileSaveError ||
      !!updateFileError,
  }

  // Unsaved changes block leaving the page (and reloading). Deleting items in the structure
  // also waits until the properties are saved.
  useBlockAction(
    STRUCTURE_BLOCK_ACTION,
    structureSaveState.isSaving || structureSaveState.isDirty ? 'provider' : undefined,
  )
  useBlockAction(
    PROPERTIES_BLOCK_ACTION,
    propertiesSaveState.isSaving || propertiesSaveState.isDirty ? 'provider' : undefined,
  )

  // Saving again after a failed save retries it with the data it failed with, then saves
  // anything pending (in that order, so newer changes land last). Failures are reported by
  // the mutations' onError.
  const saveStructure = async () => {
    try {
      if (sortError && sortVariables) await mutateSort(sortVariables)
      await flushStructure()
    } catch {
      // Errors are handled by the mutation's onError
    }
  }

  const saveProperties = async () => {
    try {
      await Promise.all([
        fileConfigError && fileConfigVariables ? mutateFileConfig(fileConfigVariables) : undefined,
        folderConfigError && folderConfigVariables
          ? mutateFolderConfig(folderConfigVariables)
          : undefined,
        fileSaveError && fileSaveVariables ? mutateFileSave(fileSaveVariables) : undefined,
        updateFileError && updateFileVariables ? mutateUpdateFile(updateFileVariables) : undefined,
      ])
      await flushProperties()
    } catch {
      // Errors are handled by the mutation's onError
    }
  }

  const { generateTemplates, generateTemplate, loadedTemplates } =
    useGenerateTemplateFiles(documentationRootGuid)

  const {
    mutateAsync: unlinkFile,
    isLoading: isUnlinkingFile,
    variables: unlinkingFileGuid,
  } = useUnlinkFile(documentationRootGuid)

  // Both work on the saved item, so pending property changes are saved first
  const generatePreview = async (file: FileItem) => {
    try {
      await flushProperties()
      await generateTemplates([file.guid])
    } catch {
      // Errors are handled by the mutations' onError
    }
  }

  // A template item is downloaded as a preview freshly generated from its current data
  const {
    mutate: downloadDocument,
    isLoading: isDownloadingDocument,
    variables: downloadingFile,
  } = useMutation({
    mutationFn: async (file: FileItem) => {
      if (file.template_key) {
        await flushProperties()
        // Also updates the preview shown in the viewer
        await generateTemplate(file.guid)
      }
      const content = await downloadFile(projectId as string, documentationRootGuid, file.guid)
      // Same type and extension as the server's download name (generated templates are Word)
      const isPdf = !file.template_key && file.mimetype === 'application/pdf'
      downLoadFile(
        content,
        isPdf
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        `${file.name}.${isPdf ? 'pdf' : 'docx'}`,
      )
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Herunterladen des Dokuments'), {
        variant: 'error',
      })
    },
  })

  const unlink = async (file: FileItem) => {
    try {
      await flushProperties()
      await unlinkFile(file.guid)
    } catch {
      // Errors are handled by the mutation's onError
    }
  }

  // Back to the page preview. Previews of every page showing an edited file are outdated
  const finishAttachmentEditing = async () => {
    // Only a Word file is editable. Whether it was saved can't be told reliably here
    // (the caller may have flushed the save already), so the tree is always reloaded
    const isFileEditable = !!shownAttachment && !isShownAttachmentPdf
    if (debouncedFileSave.isPending()) await debouncedFileSave.flush()
    setIsShowingAttachment(false)
    if (isFileEditable) {
      await queryClient.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid))
    }
  }

  const attachmentEditing: AttachmentEditing = {
    isShowingFile: isShowingAttachment,
    onShowFileChange: async isShowingFile => {
      if (isShowingFile) {
        setIsShowingAttachment(true)
        return
      }
      await finishAttachmentEditing()
    },
    onAttachmentChanged: file => {
      queryClient.setQueryData<FilesResult>(
        getProjectFiles.getKey(projectId, documentationRootGuid),
        produce<FilesResult>(draft => {
          if (!draft) return

          const index = findIndex(draft.files, { guid: file.guid })
          if (index !== -1) {
            draft.files[index].attachment = file.attachment
            draft.files[index].url = file.url
            // Attaching another file resets the page selection
            draft.files[index].additional_data = file.additional_data
          }
        }),
      )
      setEditedPageSelection(current => (current?.guid === file.guid ? null : current))
      queryClient.invalidateQueries(getProjectFiles.getKey(projectId, projectFilesRootGuid))
      // The "Dateien" templates list the project files
      queryClient.invalidateQueries(
        getProjectFilesTemplates.getKey(projectId, documentationRootGuid),
      )
      if (!file.attachment) {
        setIsShowingAttachment(false)
      }
    },
  }

  const alreadyAddedTemplates: (SectionItem | FileItem)[] = useMemo(() => {
    const addedFileTemplates = filter(documentTreeData?.files, file => !!file.template_key)
    const addedFolderTemplates = filter(documentTreeData?.sections, folder => !!folder.template_key)
    return [...addedFileTemplates, ...addedFolderTemplates]
  }, [documentTreeData])

  const {
    data: availableTemplates,
    isError: isAvailableTemplatesError,
    refetch: refetchAvailableTemplates,
  } = useAvailableTemplates({ loadOnMount: true })

  const addedTemplateGuids = useMemo(
    () => getAddedTemplateGuids(alreadyAddedTemplates),
    [alreadyAddedTemplates],
  )

  const unusedTemplates = useMemo(() => {
    if (!availableTemplates) return []

    const allItems = availableTemplates.Statikdokument.templates
    const allItemsAndChildren = allItems.flatMap(item => [
      item.guid,
      ...(item.children?.map(child => child.guid) || []),
    ])

    return reject(allItemsAndChildren, item => some(addedTemplateGuids, guid => guid === item))
  }, [availableTemplates, addedTemplateGuids])

  const loadedDocument = useMemo<ViewerDocument | null>(() => {
    if (shownAttachment?.url) {
      return {
        guid: shownAttachment.guid,
        name: shownAttachment.name,
        url: shownAttachment.url,
        fileType: isShownAttachmentPdf
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        // A Word file is edited right in the viewer
        editingDisabled: isShownAttachmentPdf,
      }
    }

    return selectedFile && selectedFile.url
      ? {
          guid: selectedFile.guid,
          name: selectedFile.name,
          url: selectedFile.url as string,
          fileType: selectedFile.mimetype as ViewerMimeTypes,
          editingDisabled: showFullDocuments || selectedFile.template_key !== null,
          watermark:
            selectedFile.template_key !== null
              ? {
                  diagonal: {
                    fontSize: 25,
                    fontFamily: 'sans-serif',
                    color: 'red',
                    opacity: 50,
                    text: 'Vorschau',
                  },
                }
              : undefined,
        }
      : null
  }, [selectedFile, showFullDocuments, shownAttachment, isShownAttachmentPdf])

  // Crop margins are shown on a PDF itself: the "Datei" tab of a markdown page
  // or an uploaded PDF (the page preview already contains the cropped PDF)
  const croppedArea = useMemo(() => {
    if (isShownAttachmentPdf) {
      const data = selectedFile?.additional_data
      return (
        cropConfig ?? {
          croppedLeft: data?.bl ?? 0,
          croppedTop: data?.bu ?? 0,
          croppedRight: data?.br ?? 0,
          croppedBottom: data?.bb ?? 0,
        }
      )
    }
    return selectedFile?.template_key ? undefined : cropConfig || undefined
  }, [isShownAttachmentPdf, selectedFile, cropConfig])

  const { triggerNotification } = useNotification()

  const { mutate: mutateFullDocCreation, isLoading } = useMutation(
    async ({ name, asPdf }: { name?: string; asPdf: boolean; runId: string }) => {
      const template: TemplatesResult = {
        templates: [
          {
            guid: 'full-doc',
            parent_guid: fullDocumentationRootGuid,
            name: name || 'Statikdokument',
            children: [],
            type: 'file',
          },
        ],
      }
      // Runs killed mid-way (reload, closed tab) leave a document that is still
      // linked to the template; clear those out before starting a new one.
      await Promise.allSettled(
        findStaleFullDocuments(mergedDocumentsData.files).map(({ guid }) =>
          deleteProjectFile.request(projectId, fullDocumentationRootGuid, guid),
        ),
      )

      const createTemplate = await addTemplates.request(
        projectId,
        fullDocumentationRootGuid,
        template,
      )

      const createdTemplate = createTemplate.templates_added[0]

      let unlinkedFile: FileItem
      try {
        const { task_id } = await generateTemplatePreview.request(
          projectId,
          fullDocumentationRootGuid,
          createdTemplate,
        )

        // retry for 60 minutes with 5 seconds interval
        await retryUntilSuccessOrFailure(
          {
            url: `/task-status/${task_id}`,
          },
          12 * 60,
          5000,
        )

        const file = await getProjectFile.request(
          projectId,
          fullDocumentationRootGuid,
          createdTemplate,
        )

        unlinkedFile = await unlinkProjectFile.request(
          projectId,
          fullDocumentationRootGuid,
          file.guid,
        )
      } catch (error) {
        // Don't leave the half-created document behind; the original error
        // is what the user needs to see, so a failed cleanup is ignored.
        await deleteProjectFile
          .request(projectId, fullDocumentationRootGuid, createdTemplate)
          .catch(() => undefined)
        throw error
      }

      if (!asPdf) return unlinkedFile

      // The server converts the stored Word file (refreshing the table of
      // contents) and saves the PDF as an additional file next to it.
      const filesBefore: FilesResult = await getProjectFiles.request(
        projectId,
        fullDocumentationRootGuid,
      )
      const { task_id: pdfTaskId } = await startConvertFileToPdfTask(
        projectId as string,
        fullDocumentationRootGuid,
        unlinkedFile.guid,
      )
      await retryUntilSuccessOrFailure({ url: `/task-status/${pdfTaskId}` }, 12 * 60, 5000)

      const filesAfter: FilesResult = await getProjectFiles.request(
        projectId,
        fullDocumentationRootGuid,
      )
      const existingGuids = new Set(filesBefore.files.map(({ guid }) => guid))
      const pdfFile = filesAfter.files.find(
        ({ guid, mimetype }) => !existingGuids.has(guid) && mimetype === 'application/pdf',
      )

      return pdfFile ?? unlinkedFile
    },
    {
      onMutate: ({ runId }) => setGeneratingRunIds(prev => [...prev, runId]),
      onSettled: (_data, _error, { runId }) =>
        setGeneratingRunIds(prev => prev.filter(id => id !== runId)),
      onSuccess: (data: FileItem) => {
        const filesKey = getProjectFiles.getKey(projectId, fullDocumentationRootGuid)

        queryClient.setQueryData<FilesResult>(filesKey, prev => {
          if (!prev) return undefined

          return {
            ...prev,
            files: [...prev.files, data],
          }
        })
        // The PDF flow also created the Word file next to it
        queryClient.invalidateQueries(filesKey)

        setShowFullDocuments(true)
        selectDocument(data.guid)
        triggerNotification({ message: 'Dokument erfolgreich zusammengeführt ✅' })
      },
      onError: (error: unknown) => {
        // A failed PDF conversion still leaves the generated Word file behind
        queryClient.invalidateQueries(getProjectFiles.getKey(projectId, fullDocumentationRootGuid))
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Erstellen des Documents'),
          { variant: 'error', persist: true },
        )
        triggerNotification({ message: 'Fehler beim Erstellen des Documents ❌' })
      },
    },
  )

  const closeFullDocumentsView = () => {
    setShowFullDocuments(false)
    selectDocument(null)
  }

  const selectDocument = async (guid: string | null) => {
    try {
      await flushAll()
      if (isShowingAttachment) {
        await finishAttachmentEditing()
      }
    } catch (error) {
      // TODO
    }
    setSelectedDocument(guid)
    setCropConfig(null)
  }

  useHotkeys(
    'ctrl+s',
    () => {
      flushAll()
    },
    { enableOnFormTags: true },
  )

  useHotkeys(
    'esc',
    event => {
      event.stopPropagation()
      selectDocument(null)
    },
    { enabled: !!selectedDocument },
  )

  const disableActions = isLoading || isLoadingDocumentTree

  return (
    <LayoutDrawer fullHeight>
      <Allotment>
        <Allotment.Pane minSize={600}>
          <Box
            sx={{
              paddingLeft: 5,
              flexGrow: 1,
            }}
          >
            <Viewer
              ref={viewerRef}
              viewerHeight="calc(100vh - 58px)"
              document={loadedDocument}
              emptyState={
                // The attached file is missing in the storage (no url)
                isShowingAttachment && !showFullDocuments ? (
                  <Info icon={<InsertDriveFile />}>Die Datei konnte nicht geladen werden</Info>
                ) : selectedFile?.template_key && !selectedFile.url && !showFullDocuments ? (
                  <PreviewPlaceholder
                    isGenerating={loadedTemplates[selectedFile.guid] === 'loading'}
                    onGeneratePreview={() => generatePreview(selectedFile)}
                  />
                ) : undefined
              }
              onDocumentLoaded={instance => {
                const loadedDoc = instance.Core.documentViewer.getDocument()
                if (loadedDoc) {
                  setViewerPageCount({
                    guid: loadedDoc.getDocumentId(),
                    pageCount: loadedDoc.getPageCount(),
                  })
                }
                const layoutMode =
                  loadedDoc && loadedDoc.getPageCount() >= 2
                    ? instance.UI.LayoutMode.FacingContinuous
                    : instance.UI.LayoutMode.Continuous

                instance.UI.setLayoutMode(layoutMode)
                instance.UI.setZoomLevel('110%')
              }}
              onChangeDocument={() => {
                const doc = viewerRef.current?.viewerInstance?.Core.documentViewer.getDocument()
                if (!doc || !selectedFile) return

                if (shownAttachment) {
                  if (isShownAttachmentPdf) return
                  debouncedFileSave({
                    doc,
                    fileGuid: shownAttachment.guid,
                    rootGuid: projectFilesRootGuid,
                    isPdf: false,
                  })
                } else if (!selectedFile.template_key) {
                  debouncedFileSave({
                    doc,
                    fileGuid: selectedFile.guid,
                    rootGuid: documentationRootGuid,
                    isPdf: selectedFile.mimetype === 'application/pdf',
                  })
                }
              }}
              additionalShortcuts={[
                {
                  key: 's',
                  action: () => debouncedFileSave.flush(),
                },
              ]}
              croppedArea={croppedArea}
              excludedPages={excludedPages}
            />
          </Box>
        </Allotment.Pane>
        <Allotment.Pane minSize={500} preferredSize="25%">
          <Box width="100%" height="100%" sx={{ backgroundColor: '#fff' }}>
            {showFullDocuments ? (
              <Stack direction="column" height="100%">
                <Box flex={1} minHeight={0} p={1} sx={{ overflow: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                    <IconButton onClick={closeFullDocumentsView} size="small" sx={{ mr: 1 }}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <FullStructuralDocumentsList
                    selectedDocument={selectedDocument}
                    onSelectDocument={selection => {
                      selectDocument(selection)
                    }}
                  />
                </Box>
                <RunningDocumentTasksFooter />
              </Stack>
            ) : (
              <Stack height="100%">
                <Box flex={1} minHeight={0}>
                  <Allotment vertical>
                    <Allotment.Pane>
                      <Stack flex={1} height="100%">
                        <Section
                          title="Struktur"
                          hideBorderBottom
                          ui2
                          titleAdornment={
                            <AutosaveIndicator
                              isSaving={structureSaveState.isSaving}
                              isLoading={structureSaveState.isLoading}
                              dirty={structureSaveState.isDirty}
                              onSaveNow={saveStructure}
                              data-cy="document-manager-structure-autosave"
                            />
                          }
                          actions={
                            <>
                              <TooltipIconButton
                                size="small"
                                tooltip="Abschnitt hinzufügen"
                                disabled={disableActions}
                                aria-label="Abschnitt hinzufügen"
                                onClick={() => setAddFolderDialogOpen(true)}
                              >
                                <CreateNewFolderOutlined fontSize="small" />
                              </TooltipIconButton>
                              <TooltipIconButton
                                size="small"
                                tooltip="Template hinzufügen"
                                disabled={disableActions}
                                aria-label="Template hinzufügen"
                                onClick={() => {
                                  // The templates load in the background when the page
                                  // opens; opening the dialog retries a failed load
                                  if (isAvailableTemplatesError) refetchAvailableTemplates()
                                  setTemplateDialogOpen(true)
                                }}
                                data-cy="btn-add-template"
                              >
                                <PlaylistAdd fontSize="small" />
                              </TooltipIconButton>
                              <TooltipIconButton
                                size="small"
                                tooltip="Datei hinzufügen"
                                disabled={disableActions}
                                aria-label="Datei hinzufügen"
                                onClick={() => setUploadDocumentParentGuid(documentationRootGuid)}
                              >
                                <NoteAddOutlined fontSize="small" />
                              </TooltipIconButton>
                              <TooltipIconButton
                                size="small"
                                tooltip="Markdown Seite hinzufügen"
                                disabled={disableActions || isAddingTemplates}
                                aria-label="Markdown Seite hinzufügen"
                                onClick={async () => {
                                  try {
                                    await mutateAddTemplates(markdownPageTemplatePayload)
                                  } catch {
                                    // Errors are handled by the mutation's onError
                                  }
                                }}
                              >
                                <PostAdd fontSize="small" />
                              </TooltipIconButton>
                            </>
                          }
                        />

                        <Stack sx={{ overflowY: 'auto', flex: 1, minHeight: 0, pb: 1.5, px: 1 }}>
                          <DropzoneBox
                            // Drops inside the (portaled) upload dialog bubble up through the React
                            // tree, so the list dropzone must not accept them while the dialog is open
                            disabled={!!uploadDocumentParentGuid || disableActions}
                            onDrop={acceptedFiles => {
                              const file = acceptedFiles[0]

                              // The file might have been deleted already. Then we do not want to use it as parent guid
                              let parentGuid = selectedDocument
                              if (selectedDocument) {
                                const selectedDocumentItemExists = find(documentTreeData.files, {
                                  guid: selectedDocument,
                                })!!

                                if (selectedDocumentItemExists) {
                                  parentGuid = selectedDocument
                                } else {
                                  parentGuid = null
                                }
                              }

                              uploadDocument({
                                file,
                                name: file.name,
                                hierarchy_position: 0,
                                parent_guid: parentGuid || documentationRootGuid,
                                guid: v4(),
                                type: 'file',
                              }).catch(() => {
                                // Errors are handled by the mutation's onError
                              })
                            }}
                            accept={{
                              'application/pdf': [],
                              'application/msword': [],
                              'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                [],
                            }}
                          >
                            <FileOrdering
                              selectedDocument={selectedDocument}
                              onSelectDocument={selection => {
                                selectDocument(selection)
                              }}
                              selectedDocumentPages={0}
                              onUploadDocument={setUploadDocumentParentGuid}
                              onSave={ordering => {
                                const queryKey = getProjectFiles.getKey(
                                  projectId,
                                  documentationRootGuid,
                                )
                                const currentData = queryClient.getQueryData(
                                  queryKey,
                                ) as FilesResult
                                const newData = produce(currentData, draft => {
                                  draft.files.forEach(file => {
                                    const order = ordering.find(order => order.guid === file.guid)
                                    if (order) {
                                      file.parent_guid = order.parent_guid
                                      file.hierarchy_position = order.hierarchy_position
                                    }
                                  })

                                  draft.sections.forEach(folder => {
                                    const order = ordering.find(order => order.guid === folder.guid)
                                    if (order) {
                                      folder.parent_guid = order.parent_guid
                                      folder.hierarchy_position = order.hierarchy_position
                                    }
                                  })
                                })
                                queryClient.setQueryData(queryKey, newData)

                                debouncedSortSave(ordering)
                              }}
                              generatingTemplates={loadedTemplates}
                              disableActions={disableActions}
                            />
                          </DropzoneBox>
                        </Stack>
                      </Stack>
                    </Allotment.Pane>

                    <Allotment.Pane visible={!!selectedDocument}>
                      <PropertiesPane
                        selectedFile={selectedFile}
                        selectedFolder={selectedSection}
                        debouncedFileConfigSave={debouncedFileConfigSave}
                        debouncedFolderConfigSave={debouncedFolderConfigSave}
                        setCropConfig={setCropConfig}
                        onUpdateFile={mutateUpdateFile}
                        attachmentEditing={attachmentEditing}
                        saveState={propertiesSaveState}
                        onSave={saveProperties}
                        isGeneratingPreview={
                          !!selectedFile && loadedTemplates[selectedFile.guid] === 'loading'
                        }
                        isUnlinking={isUnlinkingFile && unlinkingFileGuid === selectedFile?.guid}
                        onGeneratePreview={generatePreview}
                        isDownloading={
                          isDownloadingDocument && downloadingFile?.guid === selectedFile?.guid
                        }
                        onDownload={downloadDocument}
                        onUnlink={unlink}
                        pageCount={selectedFilePageCount}
                        onPagesChange={onPagesChange}
                      />
                    </Allotment.Pane>
                  </Allotment>
                </Box>
                {documentTreeData.files.length > 0 && (
                  <DocumentGenerationFooter
                    documentCount={mergedDocumentsData.files.length}
                    onShowDocuments={() => setShowFullDocuments(true)}
                    onGenerateDocument={() => setDocumentNameModalOpen(true)}
                    isGenerating={isGenerating}
                    disableGenerate={
                      structureSaveState.isSaving ||
                      structureSaveState.isDirty ||
                      propertiesSaveState.isSaving ||
                      propertiesSaveState.isDirty ||
                      isLoadingDocumentTree
                    }
                  />
                )}
              </Stack>
            )}
          </Box>

          <UploadDocumentModal
            parentGuid={uploadDocumentParentGuid ?? documentationRootGuid}
            onUploadDocument={file => {
              selectDocument(file)
              setUploadDocumentParentGuid(null)
            }}
            open={!!uploadDocumentParentGuid}
            onClose={() => setUploadDocumentParentGuid(null)}
          />

          <AddFolderModal
            parentGuid={documentationRootGuid}
            open={addFolderDialogOpen}
            onClose={() => setAddFolderDialogOpen(false)}
            onCreateFolder={folder => selectDocument(folder)}
          />

          <AddTemplateModal
            open={templateDialogOpen}
            onClose={() => setTemplateDialogOpen(false)}
            isAddingTemplates={isAddingTemplates}
            onAddTemplates={async templates => await mutateAddTemplates(templates)}
            alreadyAddedTemplates={addedTemplateGuids}
          />

          <DocumentNameModal
            open={documentNameModalOpen}
            onClose={() => setDocumentNameModalOpen(false)}
            onConfirm={(name, asPdf) => {
              mutateFullDocCreation({ name, asPdf, runId: crypto.randomUUID() })
              setDocumentNameModalOpen(false)
            }}
            unusedTemplates={unusedTemplates.length > 0}
          />
        </Allotment.Pane>
      </Allotment>
    </LayoutDrawer>
  )
}

export default DocumentManager
